import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";
import Spinner from "components/Spinner";
import { parsedJsonString } from "utils/UtilityFuncs";

const RenderProductSummary = ({
  productTypeTotals,
  allProducts,
  setAllProducts,
  refreshFunc,
  productTypesList,
  selectedProductType,
  setProductTypeSummary,
  setSelectedProductType,
}) => {
  /* ========================= All States ========================= */
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [selectedTypeName, setSelectedTypeName] = useState(null);
  const [bulkType, setBulkType] = useState("");

  /*  ========================= All Functions ========================= */

  const handleBulkUpdate = async (e, originalType, selectedType) => {
    e.preventDefault();
    try {
      let updateData = JSON.parse(JSON.stringify(allProducts));
      // The list keeps the nested `productTypes` (the W-Products table shows
      // its name), set to the new type; only the copy sent to the API drops
      // it. Deleting it from every product made each row read "No product
      // type selected" until a reload (user, 2026-11-04).
      const productsToUpdate = updateData
        .filter((product) => product?.productTypeId === originalType?.productTypeId)
        .map((product) => ({
          ...product,
          productTypeId: selectedType?.productTypeId,
          productType: selectedType?.productTypes,
          productTypes: {
            ...(product.productTypes || {}),
            productTypeId: selectedType?.productTypeId,
            productTypes: selectedType?.productTypes,
          },
        }));
      // Same order as before (the table's row order); changed rows replaced in place.
      const changedById = new Map(productsToUpdate.map((product) => [product.id, product]));
      const finalUpdateList = updateData.map((product) => changedById.get(product.id) || product);
      setLoading(true);
      const { data } = await API.patch(
        "/wo-products/bulk-update",
        { products: productsToUpdate.map(({ productTypes, ...product }) => product) },
        { headers: authHeader() },
      );
      if (data?.message?.type === "success") {
        dispatch(
          setNotification({
            message: "Products updated successfully",
            type: "success",
          }),
        );
        setSelectedProductType(selectedType);
        setAllProducts((prev) => finalUpdateList);
        if (!selectedProductType) {
          setProductTypeSummary((prev) => finalUpdateList);
        } else {
          if (refreshFunc) refreshFunc(selectedType, productsToUpdate);
        }
        setShowTypeModal(false);
        setSelectedTypeName(null);
        setBulkType("");
      } else {
        throw new Error(data?.message || "Failed to add project");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  };

  /* ========================= All UseEffects ========================= */

  return (
    <>
      <div className="w-50">
        {loading && <Spinner />}
        <h5>Product Summary</h5>
        <ul>
          {/* Alphabetical by type name. numeric so a style like "S-1036"
              orders by its number, not character by character; sensitivity
              "base" so case does not split the order. */}
          {Object.entries(productTypeTotals)
            .sort((a, b) =>
              a[0].localeCompare(b[0], undefined, {
                numeric: true,
                sensitivity: "base",
              }),
            )
            .map(([typeName, totalQty]) => (
            <li key={typeName}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <div>
                  <button
                    className="btn btn-sm btn-success py-0 px-1"
                    onClick={() => {
                      setSelectedTypeName(typeName);
                      setBulkType(typeName);
                      setShowTypeModal(true);
                    }}
                  >
                    <i className="mdi mdi-pencil"></i>
                  </button>
                </div>
                <div className="">
                  <span>
                    {typeName}&nbsp;&nbsp;:&nbsp;&nbsp;{totalQty} Qty
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
        {/* Bulk Product Type Update Modal */}
        <Modal
          show={showTypeModal}
          onHide={() => setShowTypeModal(false)}
          centered
          style={{ zIndex: 100000000 }}
          contentClassName="shadow-lg"
        >
          <Modal.Header className="py-1" closeButton>
            <Modal.Title>Bulk Update Product Type</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const originalType = productTypesList.find(
                  (pt) => pt.productTypes === selectedTypeName,
                );
                const selectedType = productTypesList.find(
                  (pt) => pt.productTypes === bulkType,
                );
                if (originalType && selectedType) {
                  handleBulkUpdate(e, originalType, selectedType);
                }
              }}
            >
              {selectedTypeName && (
                <div className="mb-3">
                  <label htmlFor="bulkTypeInput" className="form-label">
                    Update Product Type "{selectedTypeName}"
                  </label>
                  <select
                    id="bulkTypeInput"
                    className="form-select"
                    value={bulkType}
                    onChange={(e) => setBulkType(e.target.value)}
                  >
                    <option value="">Select Product Type</option>
                    {productTypesList?.map((pt) => (
                      <option key={pt.id} value={pt.productTypes}>
                        {pt.productTypes}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button className="btn btn-primary" disabled={!bulkType}>
                {loading ? "Updating..." : "Update Product Type"}
              </button>
            </form>
          </Modal.Body>
        </Modal>
      </div>
      {/*=======================================
         Bulk Style Update Modal    
      ========================================= */}
    </>
  );
};

const RenderStyleTotal = ({
  styleSummary,
  allProducts,
  setAllProducts,
  refreshFunc,
  productTypesList,
  selectedProductType,
  setProductTypeSummary,
}) => {
  /* ========================= All States ========================= */
  const dispatch = useDispatch();

  const [showStyleModal, setShowStyleModal] = useState(false);
  const [bulkStyle, setBulkStyle] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selectedStyle, setSelectedStyle] = useState(null);

  /*  ========================= All Functions ========================= */

  const handleBulkUpdate = async (e) => {
    e.preventDefault();

    try {
      let updateData = JSON.parse(JSON.stringify(allProducts)); // Remove null entries

      // Keep the nested productTypes in the list (the W-Products table shows
      // it); only the copy sent to the API drops it (user, 2026-11-04).
      const productsToUpdate = updateData
        .filter(
          (product) =>
            product?.productStyle === selectedStyle ||
            (selectedStyle === "Undefined" &&
              (!product?.productStyle ||
                product?.productStyle.toString().trim() === "")),
        )
        .map((product) => ({ ...product, productStyle: bulkStyle }));

      // Same order as before (the table's row order); changed rows replaced in place.
      const changedById = new Map(productsToUpdate.map((product) => [product.id, product]));
      const finalUpdateList = updateData.map((product) => changedById.get(product.id) || product);

      setLoading(true);

      const { data } = await API.patch(
        "/wo-products/bulk-update",
        { products: productsToUpdate.map(({ productTypes, ...product }) => product) },
        {
          headers: authHeader(),
        },
      );

      if (data?.message?.type === "success") {
        // Handle success scenario
        dispatch(
          setNotification({
            message: "Products updated successfully",
            type: "success",
          }),
        );

        setAllProducts((prev) => finalUpdateList);
        if (!selectedProductType) {
          setProductTypeSummary((prev) => finalUpdateList);
        } else {
          // get filtered products by selectedProductType
          let _filteredProducts = finalUpdateList.filter(
            (product) =>
              product?.productTypeId === selectedProductType?.productTypeId,
          );

          if (refreshFunc) refreshFunc(selectedProductType, _filteredProducts);
        }

        setShowStyleModal(false);
      } else {
        throw new Error(data?.message || "Failed to add project");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  /* ========================= All UseEffects ========================= */

  return (
    <>
      {loading && <Spinner />}
      <h5>Style Total</h5>
      <ul>
        {/* Alphabetical / numeric by style, same ordering as Product Summary. */}
        {Object.entries(styleSummary)
          .sort((a, b) =>
            a[0].localeCompare(b[0], undefined, {
              numeric: true,
              sensitivity: "base",
            }),
          )
          .map(([style, qty]) => (
          <li key={style}>
            <div className="d-flex justify-content-start align-items-center gap-2 mb-1">
              <div>
                {/* Edit Icon */}
                <button
                  className="btn btn-sm btn-success py-0 px-1"
                  onClick={() => {
                    setSelectedStyle(style);
                    setBulkStyle(style);
                    setShowStyleModal(true);
                  }}
                >
                  <i className="mdi mdi-pencil"></i>
                </button>
              </div>
              <div className="">
                {style}&nbsp;&nbsp;:&nbsp;&nbsp;{qty} Qty
              </div>
            </div>
          </li>
        ))}
      </ul>
      {/*=======================================
         Bulk Style Update Modal    
      ========================================= */}
      <Modal
        show={showStyleModal}
        onHide={() => setShowStyleModal(false)}
        centered
        // backdrop="static"
        style={{
          zIndex: 100000000,
        }}
        contentClassName="shadow-lg"
      >
        <Modal.Header className="py-1" closeButton>
          <Modal.Title>Bulk Update Style</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleBulkUpdate}>
            {selectedStyle && (
              <div className="mb-3">
                <label htmlFor="bulkStyleInput" className="form-label">
                  Update Style "{selectedStyle}"
                </label>
                <input
                  type="text"
                  autoFocus
                  id="bulkStyleInput"
                  className="form-control"
                  value={bulkStyle || ""}
                  onChange={(e) => setBulkStyle(e.target.value)}
                />
              </div>
            )}
            <button className="btn btn-primary" disabled={!bulkStyle}>
              {loading ? "Updating..." : "Update Style"}
            </button>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default function QuickViewProductSummary({
  productTypeSummary,
  selected,
  allProducts,
  setAllProducts,
  setProductsList,
  setSelectedProduct,
  setIsAllReady,
  loading,
  loadingOn,
  loadingOff,
  refreshFunc,
  productTypesList,
  selectedProductType,
  setProductTypeSummary,
  setSelectedProductType,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();
  const [contactsData, setContactsData] = useState("");

  // Per-product-type bulk status toggles
  const [summaryReady, setSummaryReady] = useState(false);
  const [summaryPurchased, setSummaryPurchased] = useState(false);
  const [summaryReceived, setSummaryReceived] = useState(false);
  const [updating, setUpdating] = useState(false);

  /*   All Functions
   ********************************************* */
  // Individual status update handlers
  const handleBulkReadyChange = async (checked) => {
    if (!selected?.id || summaryProducts.length === 0) return;
    if (loadingOn) loadingOn();
    try {
      const results = await Promise.allSettled(
        summaryProducts.map((product) =>
          API.patch(
            `/wo-products/ready-status/${product.id}`,
            { WOId: selected.id, isReady: checked },
            { headers: authHeader() },
          ),
        ),
      );
      const successfulIds = results
        .map((r, i) =>
          r.status === "fulfilled" && r.value?.data?.success
            ? summaryProducts[i].id
            : null,
        )
        .filter(Boolean);
      if (setAllProducts) {
        setAllProducts((prev = []) =>
          prev.map((p) =>
            successfulIds.includes(p.id) ? { ...p, isReady: checked } : p,
          ),
        );
      }
      if (setProductsList) {
        setProductsList((prev = []) =>
          prev.map((p) =>
            successfulIds.includes(p.id) ? { ...p, isReady: checked } : p,
          ),
        );
      }
      if (setSelectedProduct) {
        setSelectedProduct((prev) =>
          prev && successfulIds.includes(prev.id)
            ? { ...prev, isReady: checked }
            : prev,
        );
      }
      setSummaryReady(checked); // force update
      dispatch(
        setNotification({
          message: `Updated Ready for ${successfulIds.length} product${successfulIds.length > 1 ? "s" : ""}.`,
          type: "success",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      if (loadingOff) loadingOff();
    }
  };

  const handleBulkPurchasedChange = async (checked) => {
    if (!selected?.id || summaryProducts.length === 0) return;
    if (loadingOn) loadingOn();
    try {
      const results = await Promise.allSettled(
        summaryProducts.map((product) =>
          API.patch(
            "/wo-products/purchase-status",
            { id: product.id, purchased: checked, received: product.received },
            { headers: authHeader() },
          ),
        ),
      );
      const successfulIds = results
        .map((r, i) =>
          r.status === "fulfilled" && r.value?.data?.success
            ? summaryProducts[i].id
            : null,
        )
        .filter(Boolean);
      if (setAllProducts) {
        setAllProducts((prev = []) =>
          prev.map((p) =>
            successfulIds.includes(p.id) ? { ...p, purchased: checked } : p,
          ),
        );
      }
      if (setProductsList) {
        setProductsList((prev = []) =>
          prev.map((p) =>
            successfulIds.includes(p.id) ? { ...p, purchased: checked } : p,
          ),
        );
      }
      if (setSelectedProduct) {
        setSelectedProduct((prev) =>
          prev && successfulIds.includes(prev.id)
            ? { ...prev, purchased: checked }
            : prev,
        );
      }
      setSummaryPurchased(checked); // force update
      dispatch(
        setNotification({
          message: `Updated Purchased for ${successfulIds.length} product${successfulIds.length > 1 ? "s" : ""}.`,
          type: "success",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      if (loadingOff) loadingOff();
    }
  };

  const handleBulkReceivedChange = async (checked) => {
    if (!selected?.id || summaryProducts.length === 0) return;
    if (loadingOn) loadingOn();
    try {
      const results = await Promise.allSettled(
        summaryProducts.map((product) =>
          API.patch(
            "/wo-products/purchase-status",
            { id: product.id, purchased: product.purchased, received: checked },
            { headers: authHeader() },
          ),
        ),
      );
      const successfulIds = results
        .map((r, i) =>
          r.status === "fulfilled" && r.value?.data?.success
            ? summaryProducts[i].id
            : null,
        )
        .filter(Boolean);
      if (setAllProducts) {
        setAllProducts((prev = []) =>
          prev.map((p) =>
            successfulIds.includes(p.id) ? { ...p, received: checked } : p,
          ),
        );
      }
      if (setProductsList) {
        setProductsList((prev = []) =>
          prev.map((p) =>
            successfulIds.includes(p.id) ? { ...p, received: checked } : p,
          ),
        );
      }
      if (setSelectedProduct) {
        setSelectedProduct((prev) =>
          prev && successfulIds.includes(prev.id)
            ? { ...prev, received: checked }
            : prev,
        );
      }
      setSummaryReceived(checked); // force update
      dispatch(
        setNotification({
          message: `Updated Received for ${successfulIds.length} product${successfulIds.length > 1 ? "s" : ""}.`,
          type: "success",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      if (loadingOff) loadingOff();
    }
  };

  /*   All UseEffects
   ********************************************* */
  const summaryProducts = useMemo(() => {
    return Array.isArray(productTypeSummary) ? productTypeSummary : [];
  }, [productTypeSummary]);

  const productTypeTotals = useMemo(() => {
    return summaryProducts.reduce((acc, product) => {
      const typeName =
        product?.productTypes?.productTypes ||
        product?.productType ||
        "Undefined Type";

      if (!acc[typeName]) {
        acc[typeName] = 0;
      }

      acc[typeName] += product?.totalQuantity || 0;
      return acc;
    }, {});
  }, [summaryProducts, selectedProductType]);

  const styleSummary = useMemo(() => {
    return summaryProducts.reduce((acc, product) => {
      const style =
        product?.productStyle && product?.productStyle.toString().trim() !== ""
          ? product.productStyle
          : "Undefined";

      acc[style] = (acc[style] || 0) + (product?.totalQuantity || 0);
      return acc;
    }, {});
  }, [summaryProducts]);

  useEffect(() => {
    if (selected) {
      let _contactsData =
        selected?.contactData && typeof selected?.contactData === "string"
          ? parsedJsonString(selected?.contactData)
          : selected?.contactData;
      setContactsData(_contactsData);
    } else {
      setContactsData(null);
    }
  }, [selected]);

  // Sync bulk checkboxes with current summary products
  useEffect(() => {
    if (summaryProducts.length > 0) {
      setSummaryReady(summaryProducts.every((p) => !!p?.isReady));
      setSummaryPurchased(summaryProducts.every((p) => !!p?.purchased));
      setSummaryReceived(summaryProducts.every((p) => !!p?.received));
    } else {
      setSummaryReady(false);
      setSummaryPurchased(false);
      setSummaryReceived(false);
    }
  }, [summaryProducts]);

  return (
    <div>
      <Modal.Header closeButton>
        <Modal.Title className="my-0">
          <div className="d-flex flex-column w-100">
            <div>
              W-{selected?.woId} :{" "}
              {selected?.jobName?.length <= 35
                ? selected?.jobName
                : `${selected?.jobName?.slice(0, 35)}..`}
              <span className="mx-2">
                (&nbsp;
                {selectedProductType ? selectedProductType.productTypes : "All"}
                &nbsp;)
              </span>
            </div>
            <div>
              Contact: {`${contactsData?.firstName} ${contactsData?.lastName}`}
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Bulk Status Update for this product type */}
        {summaryProducts.length > 0 && setAllProducts && (
          <div className="mb-2 pb-2 border-bottom">
            {/* <h5>Bulk Update Status</h5> */}
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <label className="col-6">Purchased?</label>
                <div className="col-6 flex-center">
                  <input
                    onChange={(e) =>
                      handleBulkPurchasedChange(e.target.checked)
                    }
                    checked={summaryPurchased}
                    type="checkbox"
                    id="summaryPurchased"
                    data-switch="success"
                  />
                  <label
                    htmlFor="summaryPurchased"
                    data-on-label="Yes"
                    data-off-label="No"
                  ></label>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <label className="col-6">Received?</label>
                <div className="col-6 flex-center">
                  <input
                    onChange={(e) => handleBulkReceivedChange(e.target.checked)}
                    checked={summaryReceived}
                    type="checkbox"
                    id="summaryReceived"
                    data-switch="success"
                  />
                  <label
                    htmlFor="summaryReceived"
                    data-on-label="Yes"
                    data-off-label="No"
                  ></label>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <label className="col-4">Ready?</label>
                <div className="col-8 flex-center">
                  <input
                    onChange={(e) => handleBulkReadyChange(e.target.checked)}
                    checked={summaryReady}
                    type="checkbox"
                    id="summaryReady"
                    data-switch="success"
                  />
                  <label
                    htmlFor="summaryReady"
                    data-on-label="Yes"
                    data-off-label="No"
                  ></label>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          {summaryProducts.length > 0 ? (
            <div className="d-flex justify-content-between align-items-start">
              <RenderProductSummary
                productTypeTotals={productTypeTotals}
                allProducts={allProducts}
                setAllProducts={setAllProducts}
                refreshFunc={refreshFunc}
                productTypesList={productTypesList}
                selectedProductType={selectedProductType}
                setProductTypeSummary={setProductTypeSummary}
                setSelectedProductType={setSelectedProductType}
              />
              <div className="w-50">
                <RenderStyleTotal
                  styleSummary={styleSummary}
                  allProducts={allProducts}
                  setAllProducts={setAllProducts}
                  refreshFunc={refreshFunc}
                  productTypesList={productTypesList}
                  selectedProductType={selectedProductType}
                  setProductTypeSummary={setProductTypeSummary}
                />
              </div>
            </div>
          ) : (
            <p>No product details available.</p>
          )}
        </div>
      </Modal.Body>
    </div>
  );
}
